import React, { useMemo } from 'react';
import {
  Card,
  Frame,
  Grid,
  Divider,
  SageClassnames,
  useSageBreakpoint,
  Icon,
  SageTokens,
} from '@kajabi/sage-react';

import { COMMUNITY_LINKS } from '../../../utils/constants';
import MockCircleDiv from './MockCircleDiv';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const CommunityPreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const communityData = useMemo(() => {
    return userData.ai_data.communities;
  }, [userData]);

  const breakpoints = useSageBreakpoint();

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Community navigation component */}
        {breakpoints.sm && (
          <Frame
            background="#FFFFFF"
            style={{ height: '54px', alignItems: 'center', paddingTop: '26px' }}
            direction={Frame.DIRECTIONS.HORIZONTAL}
            padding={Frame.PADDINGS.SM}
          >
            <Frame
              padding={Frame.PADDINGS.XS}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <Frame
                style={{
                  height: '32px',
                  width: '32px',
                  backgroundImage: `
                            linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.15)),
                            url(${previewImageUrls[0]?.image_url})
                        `,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  borderRadius: '8px',
                }}
                gap={Frame.GAPS.XS}
                align={Frame.ALIGNMENTS.CENTER_CENTER}
              />
            </Frame>

            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          </Frame>
        )}

        {/* Community content component */}
        <Frame
          style={{
            backgroundColor: '#ffffff',
          }}
          padding={Frame.PADDINGS[breakpoints.md ? 'SM' : 'NONE']}
          gap={Frame.GAPS.NONE}
          align={Frame.ALIGNMENTS.TOP_CENTER}
          direction={Frame.DIRECTIONS.HORIZONTAL}
        >
          <Grid style={{ width: breakpoints.sm ? 'auto' : null }}>
            <Grid.Row
              horizontalAlignment={
                Grid.Row.HORIZONTAL_ALIGNMENT_TYPES.SPACE_BETWEEN
              }
            >
              {/* Left side */}
              {breakpoints.sm || (
                <Grid.Col
                  size={4}
                  style={{
                    paddingRight: 0,
                    borderRight: 'solid 1px #ECEEEF',
                    height: 'auto',
                  }}
                >
                  {/* Community hero image component */}
                  <Frame
                    style={{
                      minHeight: '130px',
                      backgroundImage: `
                            linear-gradient(rgba(0,0,0,.15), rgba(0,0,0,.15)),
                            url(${previewImageUrls.slice(-2)[0]?.image_url})
                        `,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                    }}
                    padding={Frame.PADDINGS.LG}
                    gap={Frame.GAPS.XS}
                    align={Frame.ALIGNMENTS.CENTER_CENTER}
                  />
                  {/* Avatar circle div */}
                  <div
                    style={{ position: 'relative', top: '-35px', left: '32px' }}
                  >
                    <MockCircleDiv size={72} color={'#E2F2FE'} />
                  </div>
                  {/* Community title and description */}
                  <Frame
                    padding={Frame.PADDINGS.LG}
                    gap={Frame.GAPS.XS}
                    style={{ marginTop: '-45px' }}
                  >
                    <p
                      className={`${SageClassnames.TYPE.BODY_MED} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                    >
                      {communityData.title}
                    </p>
                    <p
                      className={`${SageClassnames.TYPE.BODY_SMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_300}`}
                    >
                      {communityData.description}
                    </p>
                  </Frame>
                  {/* Community Links */}
                  <Frame
                    padding={Frame.PADDINGS.LG}
                    gap={Frame.GAPS.XS}
                    style={{ paddingTop: '16px' }}
                  >
                    <p className={SageClassnames.TYPE.BODY_MED}>Circles</p>
                    {COMMUNITY_LINKS.map((section: any, i: number) => (
                      <React.Fragment key={`community-links-${i}`}>
                        <p
                          className={SageClassnames.TYPE.BODY_SMALL_MED}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: '24px',
                          }}
                        >
                          <Icon
                            icon="caret-down"
                            style={{ marginRight: '8px' }}
                          />
                          {section.title}
                        </p>
                        {section.title === 'Beginners' && (
                          <div
                            style={{
                              backgroundColor:
                                SageTokens.COLOR_PALETTE.GREY_200,
                              width: '186px',
                              height: '28px',
                              display: 'flex',
                              marginBottom: '-2px',
                              marginLeft: '-4px',
                              paddingLeft: '4px',
                              borderRadius: '4px',
                            }}
                          >
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                lineHeight: '24px',
                              }}
                            >
                              <MockCircleDiv size={20} color={'#CDCFD4'} />
                              <p className={SageClassnames.TYPE.BODY_SMALL_MED}>
                                Welcome
                              </p>
                            </div>
                          </div>
                        )}
                        {section.links.map((link: any, i: number) => (
                          <div
                            key={`community-nav-link-${i}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              lineHeight: '24px',
                            }}
                          >
                            <MockCircleDiv size={20} color={'#CDCFD4'} />
                            <p className={SageClassnames.TYPE.BODY_SMALL_MED}>
                              {link}
                            </p>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </Frame>
                </Grid.Col>
              )}
              {/* Right side */}
              <Grid.Col size={8} style={{ paddingLeft: 0 }}>
                <Frame
                  gap={Frame.GAPS[breakpoints.sm ? 'SM' : 'MD']}
                  style={{
                    padding: breakpoints.sm || '48px 64px',
                    width: breakpoints.sm ? 'inherit' : null,
                    paddingLeft: breakpoints.sm ? '12px' : '64px',
                  }}
                >
                  {/* Mock buttons at top */}
                  <>
                    <Frame
                      padding={Frame.PADDINGS.NONE}
                      align={Frame.ALIGNMENTS.TOP_LEFT}
                      direction={Frame.DIRECTIONS.HORIZONTAL}
                    >
                      <div
                        style={{
                          width: 79,
                          height: 10,
                          backgroundColor: '#CDCFD4',
                          borderRadius: '100px',
                        }}
                      ></div>
                      <div
                        style={{
                          width: 79,
                          height: 10,
                          backgroundColor: '#CDCFD4',
                          borderRadius: '100px',
                        }}
                      ></div>
                      <div
                        style={{
                          width: 79,
                          height: 10,
                          backgroundColor: '#CDCFD4',
                          borderRadius: '100px',
                        }}
                      ></div>
                    </Frame>
                  </>
                  {/* Community post upload card */}
                  <Card
                    style={{
                      height: '160px',
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 4px',
                    }}
                  >
                    <Card.Row
                      gridTemplate={SageTokens.GRID_TEMPLATES.ET}
                      style={{ gap: '4px', justifyContent: 'left' }}
                    >
                      <>
                        <MockCircleDiv size={20} color={'#0072EF'} />
                        <p
                          style={{ width: 'auto' }}
                          className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_SMALL}`}
                        >
                          Write your thoughts...
                        </p>
                      </>
                    </Card.Row>
                    <Card.Footer style={{ alignItems: 'end' }}>
                      <div
                        style={{
                          width: 127,
                          height: 28,
                          backgroundColor: '#E9EBED',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '12px',
                            color: SageClassnames.TYPE_COLORS.CHARCOAL_200,
                            margin: 'auto',
                          }}
                        >
                          Add photo/video
                        </p>
                      </div>
                      <div
                        style={{
                          width: 56,
                          height: 28,
                          backgroundColor: SageTokens.COLOR_PALETTE.PRIMARY_300,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '12px',
                            color: 'white',
                            margin: 'auto',
                          }}
                        >
                          Post
                        </p>
                      </div>
                    </Card.Footer>
                  </Card>
                  {/* Community Post and Replies */}
                  <Frame gap={Frame.GAPS.SM}>
                    {communityData.posts.map((post: any, i: number) => (
                      <React.Fragment key={`community-posts-${i}`}>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: '24px',
                          }}
                        >
                          <MockCircleDiv size={20} color={'#0072EF'} />
                          <p
                            className={`${SageClassnames.TYPE_COLORS.CHARCOAL_400} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
                          >
                            {post.name}
                          </p>
                        </div>
                        {i > 0 && (
                          <Frame
                            style={{
                              minHeight: '176px',
                              backgroundImage: `
                                                  url(${heroImageUrl})
                                              `,
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: 'cover',
                              borderRadius: '8px',
                            }}
                            padding={Frame.PADDINGS.LG}
                            gap={Frame.GAPS.XS}
                            align={Frame.ALIGNMENTS.CENTER_CENTER}
                          />
                        )}
                        <p
                          className={`${SageClassnames.TYPE_COLORS.CHARCOAL_300} ${SageClassnames.TYPE.BODY_SMALL}`}
                        >
                          {post.content}
                        </p>
                        <Frame
                          style={{ paddingLeft: '24px' }}
                          gap={Frame.GAPS.XS}
                        >
                          {post.comments.map((comment: any, k: number) => (
                            <React.Fragment key={`community-post-comment-${k}`}>
                              {k > 0 && <Divider />}
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  lineHeight: '24px',
                                }}
                              >
                                <MockCircleDiv size={20} color={'#CDCFD4'} />
                                <p
                                  className={`${SageClassnames.TYPE_COLORS.CHARCOAL_400} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
                                >
                                  {comment.name}
                                </p>
                              </div>
                              <p
                                className={`${SageClassnames.TYPE_COLORS.CHARCOAL_300} ${SageClassnames.TYPE.BODY_SMALL}`}
                                style={{
                                  display: 'inline-flex',
                                  gap: '10px',
                                  paddingLeft: '26px',
                                }}
                              >
                                {comment.content}
                              </p>
                              <div
                                style={{
                                  display: 'inline-flex',
                                  gap: '10px',
                                  paddingLeft: '26px',
                                }}
                                className={SageClassnames.SPACERS.XS_BOTTOM}
                              >
                                <p
                                  className={`${SageClassnames.TYPE_COLORS.CHARCOAL_100} ${SageClassnames.TYPE.BODY_XSMALL_MED}`}
                                >
                                  Inspired
                                </p>
                                <p
                                  className={`${SageClassnames.TYPE_COLORS.CHARCOAL_100} ${SageClassnames.TYPE.BODY_XSMALL_MED}`}
                                >
                                  Reply
                                </p>
                              </div>
                            </React.Fragment>
                          ))}
                        </Frame>
                      </React.Fragment>
                    ))}
                  </Frame>
                </Frame>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Frame>
      </Frame>
    </Frame>
  );
};

export default CommunityPreview;
